import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft,
  Package,
  Clock,
  MapPin,
  FileText,
  DollarSign,
  Truck,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubmitQuotePage() {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { country } = useCountry();
  
  const [rfq, setRfq] = useState(null);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState('');
  
  const providerEmail = searchParams.get('email') || localStorage.getItem('providerEmail') || '';
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  const currency = country === 'UK' ? 'GBP' : 'LKR';
  
  const [formData, setFormData] = useState({
    quote_amount: '',
    unit_price: '',
    quantity_offered: '',
    delivery_time: '1_week',
    delivery_cost: '',
    delivery_included: false,
    validity_days: 7,
    notes: '',
    terms_conditions: '',
  });
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch RFQ details
        const rfqResponse = await fetch(`${API_URL}/api/business-services/rfq/${rfqId}`);
        const rfqData = await rfqResponse.json();
        
        if (rfqData.success) {
          setRfq(rfqData.rfq);
          setFormData(prev => ({
            ...prev,
            quantity_offered: rfqData.rfq.quantity,
          }));
        }
        
        // Fetch provider details
        if (providerEmail) {
          const providerResponse = await fetch(
            `${API_URL}/api/business-services/provider/dashboard?provider_email=${encodeURIComponent(providerEmail)}`
          );
          const providerData = await providerResponse.json();
          if (providerData.success) {
            setProvider(providerData.provider);
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load RFQ details');
      } finally {
        setLoading(false);
      }
    };
    
    if (rfqId) {
      fetchData();
    }
  }, [rfqId, providerEmail]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!provider) {
      toast.error('Provider information not found');
      return;
    }
    
    if (!formData.quote_amount || parseFloat(formData.quote_amount) <= 0) {
      toast.error('Please enter a valid quote amount');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/business-services/rfq/${rfqId}/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rfq_id: rfqId,
          provider_id: provider.id,
          provider_name: provider.business_name || provider.name,
          provider_email: providerEmail,
          provider_phone: provider.contact_phone || '+44 000 000 0000',
          quote_amount: parseFloat(formData.quote_amount),
          currency: currency,
          unit_price: formData.unit_price ? parseFloat(formData.unit_price) : null,
          quantity_offered: formData.quantity_offered,
          delivery_time: formData.delivery_time,
          delivery_cost: formData.delivery_cost ? parseFloat(formData.delivery_cost) : null,
          delivery_included: formData.delivery_included,
          validity_days: formData.validity_days,
          notes: formData.notes,
          terms_conditions: formData.terms_conditions,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setQuoteNumber(data.quote_number);
        setSubmitted(true);
        toast.success('Quote submitted successfully!');
      } else {
        toast.error(data.detail || 'Failed to submit quote');
      }
    } catch (error) {
      console.error('Failed to submit quote:', error);
      toast.error('Failed to submit quote');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!rfq) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-3xl mx-auto">
          <Card className="p-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-semibold mb-2">RFQ Not Found</h2>
            <Button onClick={() => navigate('/provider/rfq-dashboard')}>Back to Dashboard</Button>
          </Card>
        </div>
      </div>
    );
  }
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 p-6" data-testid="quote-submitted">
        <div className="max-w-3xl mx-auto">
          <Card className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-700 mb-2">Quote Submitted! 🎉</h2>
            <p className="text-gray-600 mb-4">Your quote has been sent to the customer.</p>
            <div className="bg-gray-100 rounded-lg p-4 mb-6 inline-block">
              <p className="text-sm text-gray-500">Quote Reference</p>
              <p className="text-xl font-mono font-bold">{quoteNumber}</p>
            </div>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate('/provider/rfq-dashboard')}>
                Back to Dashboard
              </Button>
              <Button onClick={() => navigate('/provider/rfq-dashboard?tab=quotes')}>
                View My Quotes
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="submit-quote-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        
        <div>
          <h1 className="text-2xl font-bold">Submit Quote</h1>
          <p className="text-gray-500">Provide your best offer for this RFQ</p>
        </div>
        
        {/* RFQ Details Card */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm font-mono text-blue-600">{rfq.rfq_number}</p>
                <h2 className="text-xl font-semibold">{rfq.title}</h2>
              </div>
              <Badge className="bg-blue-100 text-blue-700">
                {rfq.status}
              </Badge>
            </div>
            
            <p className="text-gray-700 mb-4">{rfq.item_details}</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-500" />
                <div>
                  <p className="text-xs text-gray-500">Quantity</p>
                  <p className="font-medium">{rfq.quantity} {rfq.quantity_unit}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-500" />
                <div>
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="font-medium">{rfq.delivery_location}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                <div>
                  <p className="text-xs text-gray-500">Needed By</p>
                  <p className="font-medium">{rfq.needed_by ? new Date(rfq.needed_by).toLocaleDateString() : 'Flexible'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-500" />
                <div>
                  <p className="text-xs text-gray-500">Quotes Received</p>
                  <p className="font-medium">{rfq.quotes_received || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Quote Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Your Quote
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Pricing Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700">Pricing</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="quote_amount">Total Quote Amount ({currencySymbol}) *</Label>
                    <Input
                      id="quote_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.quote_amount}
                      onChange={(e) => setFormData({ ...formData, quote_amount: e.target.value })}
                      placeholder="e.g., 2500"
                      required
                      data-testid="quote-amount"
                    />
                  </div>
                  <div>
                    <Label htmlFor="unit_price">Unit Price ({currencySymbol})</Label>
                    <Input
                      id="unit_price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.unit_price}
                      onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                      placeholder="e.g., 5"
                      data-testid="unit-price"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="quantity_offered">Quantity You Can Supply</Label>
                  <Input
                    id="quantity_offered"
                    value={formData.quantity_offered}
                    onChange={(e) => setFormData({ ...formData, quantity_offered: e.target.value })}
                    placeholder={`e.g., ${rfq.quantity}`}
                    data-testid="quantity-offered"
                  />
                </div>
              </div>
              
              {/* Delivery Section */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Delivery
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="delivery_time">Delivery Time *</Label>
                    <Select
                      value={formData.delivery_time}
                      onValueChange={(value) => setFormData({ ...formData, delivery_time: value })}
                    >
                      <SelectTrigger data-testid="delivery-time">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="same_day">Same Day</SelectItem>
                        <SelectItem value="1_3_days">1-3 Days</SelectItem>
                        <SelectItem value="1_week">1 Week</SelectItem>
                        <SelectItem value="2_weeks">2 Weeks</SelectItem>
                        <SelectItem value="custom">Custom (specify in notes)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="delivery_cost">Delivery Cost ({currencySymbol})</Label>
                    <Input
                      id="delivery_cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.delivery_cost}
                      onChange={(e) => setFormData({ ...formData, delivery_cost: e.target.value })}
                      placeholder="0 if included"
                      disabled={formData.delivery_included}
                      data-testid="delivery-cost"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="delivery_included"
                    checked={formData.delivery_included}
                    onCheckedChange={(checked) => setFormData({ 
                      ...formData, 
                      delivery_included: checked,
                      delivery_cost: checked ? '' : formData.delivery_cost,
                    })}
                    data-testid="delivery-included"
                  />
                  <Label htmlFor="delivery_included">Delivery included in quote price</Label>
                </div>
              </div>
              
              {/* Validity & Terms */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-gray-700">Validity & Terms</h3>
                
                <div>
                  <Label htmlFor="validity_days">Quote Valid For (days)</Label>
                  <Select
                    value={formData.validity_days.toString()}
                    onValueChange={(value) => setFormData({ ...formData, validity_days: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="validity-days">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="notes">Notes to Customer</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any additional information about your quote..."
                    rows={3}
                    data-testid="quote-notes"
                  />
                </div>
                
                <div>
                  <Label htmlFor="terms_conditions">Terms & Conditions</Label>
                  <Textarea
                    id="terms_conditions"
                    value={formData.terms_conditions}
                    onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                    placeholder="Payment terms, warranty info, etc."
                    rows={3}
                    data-testid="quote-terms"
                  />
                </div>
              </div>
              
              {/* Submit */}
              <div className="pt-4 border-t">
                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700" 
                  disabled={submitting}
                  data-testid="submit-quote-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Quote'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
